import { useState } from "react";
import { PopupModal } from "../../../components/Modal";
import { Switch } from "../../../components/Switch";
import { Tooltip } from "../../../components/tooltip";
import { useUpdatePricingConfig } from "../hooks";
import type {
  PricingRule,
  RuleCondition,
  RuleTimeScope,
} from "../types";
import {
  ACTION_KEYS,
  CONDITION_METRICS,
  CONDITION_OPERATORS,
  DAYS_OF_WEEK,
} from "../types";

type TimeScopeMode = "none" | "dateRange" | "singleDate" | "daysOfWeek";

function getTimeScopeMode(ts?: RuleTimeScope): TimeScopeMode {
  if (!ts) return "none";
  if (ts.date) return "singleDate";
  if (ts.days && ts.days.length > 0) return "daysOfWeek";
  if (ts.startDate || ts.endDate) return "dateRange";
  return "none";
}

function getActionBounds(key: string) {
  return ACTION_KEYS.find((a) => a.key === key);
}

export function RuleModal({
  rule,
  userId,
  onClose,
}: {
  rule?: PricingRule;
  userId: string;
  onClose: () => void;
}) {
  const isEditing = !!rule;

  const [label, setLabel] = useState(rule?.label || "");
  const [enabled, setEnabled] = useState(rule?.enabled ?? true);
  const [priority, setPriority] = useState(rule?.priority ?? 50);

  const [timeScopeMode, setTimeScopeMode] = useState<TimeScopeMode>(
    getTimeScopeMode(rule?.timeScope),
  );
  const [startDate, setStartDate] = useState(rule?.timeScope?.startDate || "");
  const [endDate, setEndDate] = useState(rule?.timeScope?.endDate || "");
  const [singleDate, setSingleDate] = useState(rule?.timeScope?.date || "");
  const [selectedDays, setSelectedDays] = useState<string[]>(
    rule?.timeScope?.days || [],
  );

  const [conditions, setConditions] = useState<RuleCondition[]>(
    rule?.conditions?.length
      ? rule.conditions
      : [{ metric: "forecastedOccupancy", operator: ">" as const, value: 50 }],
  );

  const [actions, setActions] = useState<Array<{ key: string; value: number }>>(
    rule
      ? Object.entries(rule.actions).map(([key, value]) => ({ key, value }))
      : [{ key: "boost", value: 0 }],
  );

  const updateMutation = useUpdatePricingConfig();

  const buildTimeScope = (): RuleTimeScope | undefined => {
    switch (timeScopeMode) {
      case "dateRange":
        return { startDate, endDate };
      case "singleDate":
        return { date: singleDate };
      case "daysOfWeek":
        return { days: selectedDays };
      default:
        return undefined;
    }
  };

  const handleSave = () => {
    const actionsObj: Record<string, number> = {};
    for (const a of actions) {
      if (a.key) actionsObj[a.key] = a.value;
    }

    const timeScope = buildTimeScope();
    const hasTimeScope =
      timeScope &&
      (timeScope.date ||
        timeScope.startDate ||
        (timeScope.days && timeScope.days.length > 0));
    const hasConditions = conditions.length > 0;

    if (!hasTimeScope && !hasConditions) {
      return;
    }

    if (Object.keys(actionsObj).length === 0) {
      return;
    }

    const ruleData: any = {
      ...(rule?.id ? { id: rule.id } : {}),
      label: label || undefined,
      enabled,
      priority,
      timeScope: hasTimeScope ? timeScope : undefined,
      conditions: hasConditions ? conditions : undefined,
      actions: actionsObj,
    };

    updateMutation.mutate(
      {
        userId,
        data: {
          scope: {
            type: "rule",
            ...(rule?.id ? { ruleId: rule.id } : {}),
          },
          rule: ruleData,
          reasoning: isEditing
            ? `Updated rule: ${label || rule?.label}`
            : `Created rule: ${label || "Untitled"}`,
          trigger: "admin",
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  const addCondition = () => {
    if (conditions.length >= 4) return;
    setConditions([
      ...conditions,
      { metric: "forecastedOccupancy", operator: ">", value: 50 },
    ]);
  };

  const updateCondition = (index: number, updates: Partial<RuleCondition>) => {
    setConditions(
      conditions.map((c, i) => (i === index ? { ...c, ...updates } : c)),
    );
  };

  const removeCondition = (index: number) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  const addAction = () => {
    const usedKeys = actions.map((a) => a.key);
    const available = ACTION_KEYS.find((ak) => !usedKeys.includes(ak.key));
    if (!available) return;
    setActions([...actions, { key: available.key, value: 0 }]);
  };

  const updateAction = (
    index: number,
    updates: Partial<{ key: string; value: number }>,
  ) => {
    setActions(
      actions.map((a, i) => (i === index ? { ...a, ...updates } : a)),
    );
  };

  const removeAction = (index: number) => {
    if (actions.length <= 1) return;
    setActions(actions.filter((_, i) => i !== index));
  };

  const toggleDay = (day: string) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
    );
  };

  const hasTimeScope =
    timeScopeMode !== "none" &&
    (timeScopeMode === "daysOfWeek"
      ? selectedDays.length > 0
      : timeScopeMode === "singleDate"
        ? !!singleDate
        : !!(startDate || endDate));
  const hasConditions = conditions.length > 0;
  const hasActions = actions.some((a) => a.key);
  const canSave = (hasTimeScope || hasConditions) && hasActions;

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="mx-auto max-w-[600px] w-full"
    >
      <h3 className="text-lg font-semibold text-content-primary">
        {isEditing ? "Edit Rule" : "Add Rule"}
      </h3>

      <div className="space-y-5 max-h-[65vh] overflow-auto pr-1">
        {/* Label & Enabled */}
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-content-primary mb-1">
              Label
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value.slice(0, 100))}
              className="w-full px-3 py-2 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="e.g. Weekend boost, Low occupancy discount..."
            />
          </div>
          <div className="flex items-center gap-2 pt-5">
            <span className="text-sm text-content-secondary">Enabled</span>
            <Switch
              checked={enabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setEnabled(e.target.checked)
              }
            />
          </div>
        </div>

        {/* Priority */}
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <label className="text-sm font-medium text-content-primary">
              Priority
            </label>
            <Tooltip id="tt-rule-priority">
              <p className="text-xs">
                Lower numbers run first. Priority 1 executes before priority 99.
                When rules conflict, later rules override earlier ones.
              </p>
            </Tooltip>
          </div>
          <input
            type="number"
            value={priority}
            onChange={(e) => {
              const v = parseInt(e.target.value);
              if (!isNaN(v)) setPriority(Math.min(100, Math.max(1, v)));
            }}
            min={1}
            max={100}
            className="w-[100px] px-3 py-2 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Time Scope */}
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <label className="text-sm font-medium text-content-primary">
              When does this rule apply?
            </label>
            <Tooltip id="tt-rule-timescope">
              <p className="text-xs">
                Choose when this rule should be active. Must have time scope or
                conditions (or both).
              </p>
            </Tooltip>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {(
              [
                ["none", "Always"],
                ["dateRange", "Date Range"],
                ["singleDate", "Specific Date"],
                ["daysOfWeek", "Days of Week"],
              ] as const
            ).map(([mode, modeLabel]) => (
              <button
                key={mode}
                onClick={() => setTimeScopeMode(mode)}
                className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
                  timeScopeMode === mode
                    ? "bg-primary text-white border-primary"
                    : "bg-surface-secondary text-content-secondary border-border-primary hover:bg-surface-hover"
                }`}
              >
                {modeLabel}
              </button>
            ))}
          </div>

          {timeScopeMode === "dateRange" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <span className="text-sm text-content-tertiary">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          )}

          {timeScopeMode === "singleDate" && (
            <input
              type="date"
              value={singleDate}
              onChange={(e) => setSingleDate(e.target.value)}
              className="px-3 py-2 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            />
          )}

          {timeScopeMode === "daysOfWeek" && (
            <div className="flex gap-1.5">
              {DAYS_OF_WEEK.map(({ value, label: dayLabel }) => (
                <button
                  key={value}
                  onClick={() => toggleDay(value)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded border transition-colors ${
                    selectedDays.includes(value)
                      ? "bg-primary text-white border-primary"
                      : "bg-surface-secondary text-content-secondary border-border-primary hover:bg-surface-hover"
                  }`}
                >
                  {dayLabel}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Conditions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-content-primary">
                Conditions
              </label>
              <Tooltip id="tt-rule-conditions">
                <p className="text-xs">
                  When all conditions are met, the rule applies. Max 4
                  conditions.
                </p>
              </Tooltip>
            </div>
            <button
              onClick={addCondition}
              disabled={conditions.length >= 4}
              className="text-xs font-medium text-primary hover:opacity-80 disabled:opacity-40"
            >
              + Add
            </button>
          </div>

          {conditions.map((cond, i) => {
            const metricDef = CONDITION_METRICS.find(
              (m) => m.key === cond.metric,
            ) || CONDITION_METRICS[0];

            return (
              <div
                key={i}
                className="flex items-center gap-2 mb-2 bg-surface-secondary rounded px-3 py-2"
              >
                <select
                  value={cond.metric}
                  onChange={(e) =>
                    updateCondition(i, { metric: e.target.value })
                  }
                  className="px-2 py-1 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-surface-primary"
                >
                  {CONDITION_METRICS.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <select
                  value={cond.operator}
                  onChange={(e) =>
                    updateCondition(i, { operator: e.target.value as any })
                  }
                  className="px-2 py-1 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-surface-primary"
                >
                  {CONDITION_OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
                <div className="relative">
                  <input
                    type="number"
                    value={cond.value}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v))
                        updateCondition(i, {
                          value: Math.min(
                            metricDef.max,
                            Math.max(metricDef.min, v),
                          ),
                        });
                    }}
                    min={metricDef.min}
                    max={metricDef.max}
                    className={`w-[70px] px-2 py-1 border border-border-primary rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary ${metricDef.suffix ? "pr-6" : ""}`}
                  />
                  {metricDef.suffix && (
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-content-tertiary pointer-events-none">
                      {metricDef.suffix}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => removeCondition(i)}
                  disabled={conditions.length <= 1}
                  className="text-xs text-red-500 hover:text-red-700 ml-auto disabled:opacity-30"
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <label className="text-sm font-medium text-content-primary">
                Actions
              </label>
              <Tooltip id="tt-rule-actions">
                <p className="text-xs">
                  Override config values when this rule matches. At least one
                  action is required. Values are auto-clamped to valid bounds.
                </p>
              </Tooltip>
            </div>
            <button
              onClick={addAction}
              disabled={actions.length >= ACTION_KEYS.length}
              className="text-xs font-medium text-primary hover:opacity-80 disabled:opacity-40"
            >
              + Add
            </button>
          </div>

          {actions.map((action, i) => {
            const bounds = getActionBounds(action.key);
            const usedKeys = actions
              .map((a, idx) => (idx !== i ? a.key : null))
              .filter(Boolean);

            return (
              <div
                key={i}
                className="flex items-center gap-2 mb-2 bg-surface-secondary rounded px-3 py-2"
              >
                <select
                  value={action.key}
                  onChange={(e) => updateAction(i, { key: e.target.value })}
                  className="flex-1 px-2 py-1 border border-border-primary rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary bg-surface-primary"
                >
                  {ACTION_KEYS.map((ak) => (
                    <option
                      key={ak.key}
                      value={ak.key}
                      disabled={usedKeys.includes(ak.key)}
                    >
                      {ak.label}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  value={action.value}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    if (!isNaN(v)) updateAction(i, { value: v });
                  }}
                  step={0.1}
                  className="w-[80px] px-2 py-1 border border-border-primary rounded text-sm text-right focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {bounds?.showRange && (
                  <span className="text-[10px] text-content-tertiary whitespace-nowrap">
                    {bounds.min}–{bounds.max}
                  </span>
                )}
                <button
                  onClick={() => removeAction(i)}
                  disabled={actions.length <= 1}
                  className="text-xs text-red-500 hover:text-red-700 disabled:opacity-30"
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>

        {/* Validation hint */}
        {!canSave && (
          <p className="text-xs text-red-500">
            A rule needs at least one action, and either a time scope or
            condition.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-content-secondary bg-surface-secondary rounded hover:bg-surface-hover"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={!canSave || updateMutation.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:opacity-90 disabled:opacity-50"
        >
          {updateMutation.isPending
            ? "Saving..."
            : isEditing
              ? "Update Rule"
              : "Add Rule"}
        </button>
      </div>
    </PopupModal>
  );
}
